import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createSelector } from "reselect";
import "./Post.css";
import CommentBox from "./CommentBox/CommentBox";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
// import { collection, query, where, getCountFromServer } from 'firebase/firestore';
// import { db } from '../../../../src/firebaseConfig';
import SolutionDisplay from "./solutions/SolutionDisplay";
import { extractMetadata } from "../../../utils/linkPreview";
import { lazyLoadManager } from "../../../utils/performanceOptimizations";
import { useFeedRefresh } from "../../../features/feed";
import {
  togglePostLike,
  checkPostLike,
  savePostToLocal,
  unsavePostFromLocal,
  updatePost,
} from "../../../redux/slices/postSlice";

import {
  toggleCommentBox,
  setOpenSolutionsPostId,
  openDeleteConfirm,
} from "../../../redux/slices/uiSlice";
import {
  openShareModal,
} from "../../../features/share";
import {
  openReportModal,
} from "../../../redux/slices/modalSlice";
import { selectSolutionsTotalCount } from "../../../redux/solutions";
import PostHeader from "./PostHeader.jsx";
import PostContent from "./PostContent.jsx";
import PostFooter from "./PostFooter.jsx";
import MentionAutocomplete from "../../../components/MentionAutocomplete";
import { injectStructuredMentions, stripStructuredMentions } from "../../../utils/mentionInjection.js";

import { useNotificationTriggers } from "../../../features/notifications";

// Memoized selectors - avoid identity function pattern by always transforming
const selectPostLikeStatus = createSelector(
  [(state, postId) => state.posts.postLikeStatus?.[postId]],
  (postLikeStatus) => ({
    isLiked: postLikeStatus?.isLiked ?? false,
    likes: postLikeStatus?.likes ?? 0,
  }),
);

// Issue 2 Fix: Include pending deltas for accurate real-time count (includes nested replies)
const selectPostCommentCount = createSelector(
  [
    (state, postId) =>
      state.posts.posts?.find((post) => post.id === postId)?.commentCount,
    (state, postId) => state.posts.pendingCommentCountDelta?.[postId] || 0,
  ],
  (commentCount, pendingDelta) => Math.max(0, (commentCount ?? 0) + pendingDelta),
);

// Selector to get the specific post's share count
const selectPostSharesCount = createSelector(
  [
    (state, postId) =>
      state.posts.posts?.find((post) => post.id === postId)?.shares,
  ],
  (shares) => shares ?? 0,
);

const selectPostLikeCount = createSelector(
  [
    (state, postId) =>
      state.posts.posts?.find((post) => post.id === postId)?.likeCount,
  ],
  (likeCount) => (typeof likeCount === "number" ? likeCount : 0),
);

// Removed selectCommentsByPostId as it's no longer used for the count

function Post({
  username,
  profession,
  timestamp,
  avatar = null,
  content,
  actionTag = "default",
  postId,
  title = "",
  imageUrl = "",
  imageUrls = [],
  link = "",
  issue = null,
  scale = "",
  location: postLocation = "", // Renamed to postLocation to avoid conflict with useLocation
  issueStatus = "",
  onUnsave = () => { },
  userId, // Add userId prop
  highlightTokens = [], // Optional: tokens to highlight in title/content when shown in search results
  projectId,
  hasProject,
  sharedPostData,
  sharedPostId,
  type,
  hideFooter = false,
  isHighlighted = false, // For trending post highlight animation
  preventAutoScroll = false, // Prevent inner posts in reshares from triggering scroll
  linkPreviewData = null, // Stored link preview from post creation
  isLinkPost = false, // Flag indicating post contains a link
  hideSettings = false, // Disable edit/delete/report options
  isInsideReshare = false, // Styling/Interaction flag for modal previews
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const postRootRef = useRef(null);
  const postLikeStatus = useSelector((state) =>
    selectPostLikeStatus(state, postId),
  );
  // const commentsByPostId = useSelector(state => selectCommentsByPostId(state, postId)); // REMOVED
  const commentCount = useSelector((state) =>
    selectPostCommentCount(state, postId),
  ); // Get the comment count directly
  const shareCount = useSelector((state) =>
    selectPostSharesCount(state, postId),
  ); // Get the share count
  const likeCountFallback = useSelector((state) =>
    selectPostLikeCount(state, postId),
  );
  const savedPosts = useSelector((state) => state.posts.savedPosts);
  const showCommentBox = useSelector(
    (state) => state.ui.showCommentBox[postId] || false,
  );
  const currentUser = useSelector((state) => state.auth.currentUser);
  const openSolutionsPostId = useSelector(
    (state) => state.ui.openSolutionsPostId,
  );
  const { priorityPostId, clearHighlight } = useFeedRefresh();

  const [showFullContent, setShowFullContent] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(title || "");
  const [editContent, setEditContent] = useState(content || "");
  const [editMentionMap, setEditMentionMap] = useState({});
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  // Commented out unused state variables to clean up the code
  // const [previewComments, setPreviewComments] = useState([]);
  const [isSaved, setIsSaved] = useState(false);
  const [linkPreview, setLinkPreview] = useState(null);
  // const [commentReplies, setCommentReplies] = useState({});
  const [showImageModal, setShowImageModal] = useState(false);
  const [isHoveringHeaderRight, setIsHoveringHeaderRight] = useState(false);
  const [solutionsCount, setSolutionsCount] = useState(0);
  const solutionsTotalFromState = useSelector((state) =>
    selectSolutionsTotalCount(state, postId),
  );
  const [isLikeProcessing, setIsLikeProcessing] = useState(false);
  // const [showCommentPreview, setShowCommentPreview] = useState(false);

  // Combine like status with processing state for PostFooter
  const postLikeStatusWithProcessing = useMemo(
    () => ({
      ...postLikeStatus,
      isLikeProcessing,
    }),
    [postLikeStatus, isLikeProcessing],
  );

  const solutionsRef = useRef(null);
  const [inView, setInView] = useState(false);
  const didCheckLikeRef = useRef(false);
  const lastScrolledHashRef = useRef(null);

  const { triggerLikeNotification } = useNotificationTriggers();

  // When global openSolutionsPostId toggles to this post, scroll into view
  useEffect(() => {
    if (openSolutionsPostId === postId && solutionsRef.current) {
      solutionsRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [openSolutionsPostId, postId]);

  // Clear highlight after CSS animation finishes (race-proof against async fetchSinglePost)
  // Note: We do NOT call clearHighlight here — the JS timer in FeedRefreshContext handles it.
  // This avoids the flicker caused by removing the animation class mid-render-cycle.
  useEffect(() => {
    if (!isHighlighted || !postRootRef.current) return;
    // No-op: highlight clearing is handled by the FeedRefreshContext timer
  }, [isHighlighted]);

  // Observe visibility of the post to defer heavy work (e.g., link preview)
  useEffect(() => {
    const el = postRootRef.current;
    if (!el) return;
    const cb = () => setInView(true);
    try {
      lazyLoadManager.observe(el, cb);
    } catch {
      setInView(true);
    }
    return () => {
      try {
        lazyLoadManager.unobserve(el);
      } catch { }
    };
  }, []);

  useEffect(() => {
    if (!inView) return;
    if (!currentUser?.uid) return;
    if (didCheckLikeRef.current) return;
    didCheckLikeRef.current = true;
    dispatch(checkPostLike({ postId, userId: currentUser.uid }));
  }, [inView, currentUser, postId, dispatch]);
  // Consolidated navigation/scroll logic
  useEffect(() => {
    // Skip scroll for inner posts in reshares - they have the same postId as original
    if (preventAutoScroll) return;

    const state = location.state || {};
    const params = new URLSearchParams(location.search);
    const postParam = params.get("post");
    const isMatchingPost =
      state.scrollToPost === postId || postParam === postId;

    if (!isMatchingPost) return;

    let didHandle = false;

    // Always scroll the matching post into view
    try {
      if (postRootRef.current) {
        // If this is the priority post, the feed already scrolled to the top.
        // We only need to scroll if it's NOT the priority post (e.g. middle of feed)
        // or if we are expanding solutions.
        const isAtTop = priorityPostId === postId;

        if (!isAtTop) {
          // Execute scroll alignment instantly before the next paint
          // Avoid 500ms arbitrary delays to eliminate "scroll yank" behavior.
          if (postRootRef.current) {
            try {
              postRootRef.current.scrollIntoView({
                behavior: "auto",
                block: "start",
              });
            } catch (e) {
              /* ignore */
            }
          }
        }
        didHandle = true;
      }
    } catch (error) {
      console.error("❌ Scroll error:", error);
    }

    // Expand solutions if flagged via hash or state context
    const shouldExpand =
      location.hash.startsWith("#solution-") ||
      state.scrollToSolutions ||
      state.shouldExpandSolutions;

    if (shouldExpand) {
      // Track handled hashes to prevent scroll-lock/loops when location.state clears
      const currentHash = location.hash;
      const alreadyScrolledHash = lastScrolledHashRef.current === currentHash;

      if (
        solutionsRef.current &&
        (!alreadyScrolledHash ||
          state.scrollToSolutions ||
          state.shouldExpandSolutions)
      ) {
        solutionsRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        lastScrolledHashRef.current = currentHash;
      }

      // Ensure comment box is closed for this post to maintain mutual exclusivity
      if (showCommentBox) {
        dispatch(toggleCommentBox(postId));
      }
      dispatch(setOpenSolutionsPostId(postId));
      didHandle = true;
    }

    if (didHandle && (state.scrollToSolutions || state.shouldExpandSolutions)) {
      const newState = { ...state };
      delete newState.scrollToPost;
      delete newState.scrollToSolutions;
      delete newState.shouldExpandSolutions;
      // preserve solution hash for SolutionDisplay to catch
      navigate(location.pathname + location.search + location.hash, {
        replace: true,
        state: newState,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.state,
    location.search,
    location.hash,
    location.pathname,
    postId,
    navigate,
    dispatch,
    showCommentBox,
  ]); // preventAutoScroll intentionally excluded

  // Check if post is saved on mount and when savedPosts changes
  useEffect(() => {
    if (savedPosts) {
      const saved = savedPosts.some((saved) => saved.id === postId);
      setIsSaved(saved);
    }
  }, [savedPosts, postId]);

  // Sync solutions count from Redux (updated by SolutionDisplay fetch)
  useEffect(() => {
    if (typeof solutionsTotalFromState === "number") {
      setSolutionsCount(solutionsTotalFromState);
    }
  }, [solutionsTotalFromState]);

  // Extract link preview when content/link changes and post is in/near viewport
  useEffect(() => {
    if (!inView) return;
    const fetchLinkPreview = async () => {
      let urlToExtract = link;
      if (!urlToExtract && content) {
        const urlRegex = /((https?:\/\/|www\.)[^\s]+)/g;
        const matches = content.match(urlRegex);
        if (matches && matches[0]) {
          urlToExtract = matches[0];
        }
      }

      if (urlToExtract) {
        // Let extractMetadata sanitize/validate
        try {
          const metadata = await extractMetadata(urlToExtract);
          setLinkPreview(metadata);
        } catch (error) {
          console.error("Error extracting link metadata:", error);
          setLinkPreview(null); // Clear preview on error
        }
      } else {
        setLinkPreview(null);
      }
    };

    fetchLinkPreview();
  }, [content, link, inView]); // Depend on both content/link and visibility

  // Get the post author's ID from the post data
  const postAuthorId = userId; // Use the userId prop directly

  const canManage = !!currentUser && currentUser.uid === postAuthorId;

  const handleDeletePost = () => {
    if (!canManage) return;
    dispatch(openDeleteConfirm({ itemId: postId, itemType: "post" }));
  };

  const handleEditPost = () => {
    if (!canManage) return;
    const { plainText, mentionMap } = stripStructuredMentions(content || "");
    setEditTitle(title || "");
    setEditContent(plainText);
    setEditMentionMap(mentionMap);
    setIsEditing(true);
  };

  const handleEditMentionSelect = (user) => {
    if (!user || !user.username || !user.id) return;
    setEditMentionMap(prev => ({ ...prev, [user.username]: user.id }));
  };

  const handleSaveEdit = async () => {
    if (!canManage) return;
    const newTitle = (editTitle || "").trim();
    const newContent = (editContent || "").trim();
    if (!newTitle || !newContent) {
      toast.warning("Title and content are required");
      return;
    }
    const injectedContent = injectStructuredMentions(newContent, editMentionMap);
    try {
      setIsSavingEdit(true);
      await dispatch(
        updatePost({
          postId,
          updates: { title: newTitle, content: injectedContent },
        }),
      ).unwrap();
      toast.success("Post updated");
      setIsEditing(false);
      setEditMentionMap({});
    } catch (e) {
      toast.error(e?.message || "Failed to update post");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleMessageClick = () => {
    if (!currentUser) {
      toast.info("Please log in to comment");
      return;
    }
    if (showCommentBox) {
      dispatch(toggleCommentBox(postId));
      return;
    }
    if (openSolutionsPostId === postId) {
      dispatch(setOpenSolutionsPostId(null));
    }
    // Only toggle visibility — CommentBox's own useEffect handles the fetch.
    // The old pre-warm dispatch(fetchPostComments({ postId, limit: 3 })) was removed
    // because it raced with CommentBox's loadComments (limit: 5), causing two
    // simultaneous fetches, a hasMore mismatch (3 vs 5 page size), and a loading
    // skeleton flash on every open.
    dispatch(toggleCommentBox(postId));
  };

  // Ensure mutual exclusivity: opening solutions closes this post's comment box
  const handleSolutionsClick = () => {
    if (openSolutionsPostId === postId) {
      // Closing solutions for this post
      dispatch(setOpenSolutionsPostId(null));
    } else {
      // Opening solutions for this post: close its comment box first
      if (showCommentBox) {
        dispatch(toggleCommentBox(postId));
      }
      dispatch(setOpenSolutionsPostId(postId));
      // Push history entry so Back button returns to expanded state (N2/XB3 fix)
      navigate(location.pathname + location.search + '#solutions-' + postId, {
        replace: false,
      });
    }
  };

  const handlePostLike = () => {
    if (isLikeProcessing || !currentUser) {
      if (!currentUser) toast.info("Please log in to like posts");
      return;
    }

    setIsLikeProcessing(true);
    const wasLiked = !!postLikeStatus?.isLiked;
    dispatch(togglePostLike({ postId, userId: currentUser.uid }))
      .unwrap()
      .then((result) => {
        if (
          !wasLiked &&
          result?.isLiked &&
          postAuthorId &&
          currentUser?.uid !== postAuthorId
        ) {
          Promise.resolve(
            triggerLikeNotification(
              { id: postId, content, title },
              postAuthorId,
            ),
          ).catch(() => { });
        }
      })
      .catch((error) => {
        console.error("Error liking post:", error);
        toast.error("Failed to process like. Please try again.");
      })
      .finally(() => {
        setTimeout(() => setIsLikeProcessing(false), 200);
      });
  };

  const handleShare = () => {
    // Issue #9: When sharing a reshared post, share the original post instead
    // This prevents nested reshare chains
    if (type === "shared_post" && sharedPostData && sharedPostId) {
      // For reshared posts, validate using the original post's data
      const originalContent =
        sharedPostData.content || sharedPostData.title || "";

      if (!sharedPostId || !originalContent) {
        toast.error("Unable to share post. Missing required information.");
        return;
      }

      dispatch(
        openShareModal({
          item: {
            id: sharedPostId,
            ...sharedPostData,
            // Ensure we have the original post's data
            type: "post", // Mark as regular post, not shared_post
          },
          type: "post",
        }),
      );
      return;
    }

    // Regular post validation
    if (!postId || !content || !username) {
      toast.error("Unable to share post. Missing required information.");
      return;
    }

    dispatch(
      openShareModal({
        item: {
          id: postId,
          content: content || "",
          username: username || "",
          profession: profession || "",
          actionTag: actionTag || "",
          avatar,
          timestamp,
          title,
          imageUrl,
          imageUrls,
          link,
          issue,
          scale,
          location: postLocation,
          issueStatus,
          userId,
          projectId,
          hasProject,
          type,
          sharedPostData,
          sharedPostId,
        },
        type: "post",
      }),
    );
  };

  const handleSave = async () => {
    if (!currentUser) {
      toast.info("Please log in to save posts");
      return;
    }

    try {
      if (isSaved) {
        await dispatch(unsavePostFromLocal(postId)).unwrap();
        toast.success("Post removed from saved items");
        onUnsave(postId);
      } else {
        const postToSave = {
          id: postId,
          username,
          userProfession: profession,
          content,
          actionTag,
          timestamp,
          title,
          imageUrl,
          link,
          issue,
          scale,
          location: postLocation,
          issueStatus,
        };
        await dispatch(savePostToLocal(postToSave)).unwrap();
        toast.success("Post saved successfully");
      }
    } catch (error) {
      console.error("Error saving/unsaving post:", error);
      toast.error(isSaved ? "Failed to unsave post" : "Failed to save post");
    }
  };

  const handleReport = () => {
    if (!currentUser) {
      toast.info("Please log in to report posts");
      return;
    }
    dispatch(
      openReportModal({
        itemId: postId,
        itemType: "post",
        postAuthorId: userId,
      }),
    );
  };

  const handlePostClick = (e) => {
    // Check if the click is inside the solutions section or on a link/button
    const isClickInSolutions = e.target.closest(".solutions-section") !== null;
    const isClickOnInteractiveElement =
      e.target.tagName === "A" ||
      e.target.tagName === "BUTTON" ||
      e.target.closest("button") !== null ||
      e.target.closest("a") !== null ||
      e.target.closest('[data-mention]') !== null;

    // Don't handle clicks inside the solutions section or on interactive elements
    if (isClickInSolutions || isClickOnInteractiveElement) {
      return;
    }
  };

  return (
    <div
      className={`post${isHighlighted ? " highlighted-trending" : ""}${isInsideReshare ? " is-inside-reshare" : ""}`}
      ref={postRootRef}
      onClick={handlePostClick}
    >
      <PostHeader
        username={username}
        profession={profession}
        timestamp={timestamp}
        avatar={avatar}
        actionTag={actionTag}
        issue={issue}
        scale={scale}
        location={postLocation}
        issueStatus={issueStatus}
        isHoveringHeaderRight={isHoveringHeaderRight}
        setIsHoveringHeaderRight={setIsHoveringHeaderRight}
        postAuthorId={postAuthorId}
        hasProject={hasProject}
        projectId={projectId}
        type={type}
        sharedPostId={sharedPostId}
        hideSettings={hideSettings}
        isInsideReshare={isInsideReshare}
        currentUser={currentUser}
      />

      {!isEditing && (
        <PostContent
          title={title}
          imageUrl={imageUrl}
          imageUrls={imageUrls}
          content={content}
          linkPreview={linkPreview}
          showImageModal={showImageModal}
          setShowImageModal={setShowImageModal}
          showFullContent={showFullContent}
          setShowFullContent={setShowFullContent}
          highlightTokens={highlightTokens}
          sharedPostData={sharedPostData}
          sharedPostId={sharedPostId}
          type={type}
          linkPreviewData={linkPreviewData}
          isLinkPost={isLinkPost}
          isInsideReshare={isInsideReshare}
          username={username}
          profession={profession}
          timestamp={timestamp}
          avatar={avatar}
        />
      )}

      {isEditing && (
        <div
          className="post__content"
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            padding: 12,
            marginBottom: 8,
          }}
        >
          <input
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            placeholder="Title"
            style={{
              width: "100%",
              padding: "8px 10px",
              marginBottom: 8,
              border: "1px solid #d1d5db",
              borderRadius: 6,
            }}
            maxLength={50}
          />
          <MentionAutocomplete
            value={editContent}
            onChange={setEditContent}
            onMentionSelect={handleEditMentionSelect}
            mentionNames={Object.keys(editMentionMap)}
            placeholder="Update your content"
            style={{
              width: "100%",
              minHeight: 120,
              padding: 10,
              border: "1px solid #d1d5db",
              borderRadius: 6,
            }}
            className="post-edit-textarea"
          />
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button
              className="read-more-button"
              onClick={() => setIsEditing(false)}
              disabled={isSavingEdit}
            >
              Cancel
            </button>
            <button
              className="read-more-button"
              onClick={handleSaveEdit}
              disabled={isSavingEdit}
            >
              {isSavingEdit ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      )}

      {!hideFooter && (
        <PostFooter
          postId={postId}
          postLikeStatus={postLikeStatusWithProcessing}
          fallbackLikeCount={likeCountFallback}
          handlePostLike={handlePostLike}
          handleMessageClick={handleMessageClick}
          handleShare={handleShare}
          shareCount={shareCount}
          isSaved={isSaved}
          handleSave={handleSave}
          currentUser={currentUser}
          commentCount={commentCount}
          solutionsCount={solutionsCount}
          onSolutionsClick={handleSolutionsClick}
          showSolutions={openSolutionsPostId === postId}
          handleReport={handleReport}
          showCommentBox={showCommentBox}
          canManage={canManage}
          onEditPost={handleEditPost}
          onDeletePost={handleDeletePost}
        />
      )}

      {/* Solutions section - hide for inner posts (hideFooter) to prevent duplicate display */}
      {!hideFooter && openSolutionsPostId === postId && (
        <div
          ref={solutionsRef}
          id={`solutions-${postId}`}
          className="solutions-section"
        >
          <SolutionDisplay
            postId={postId}
            onSolutionCountChange={setSolutionsCount}
          />
        </div>
      )}

      {/* Comments section - hide for inner posts (hideFooter) to prevent duplicate display */}
      {!hideFooter && showCommentBox && openSolutionsPostId !== postId && (
        <CommentBox postId={postId} isVisible={showCommentBox} />
      )}
    </div>
  );
}
export default React.memo(Post);
